import Select from '@mui/material/Select'
import { type CSSObject, type Theme } from '@mui/material/styles'
import { styled } from '../../../utils/styled'
import { type color } from './types'

export const StyledSelectContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth'
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  ...(fullWidth !== true ? { width: '480px' } : {})
}))

const defaultFieldsetBorder = (theme: Theme): string => `1px solid ${theme.tokens.border.main.tonal}`

const focusFieldset = (theme: Theme): CSSObject => ({
  border: `1px solid ${theme.tokens.border.focus.brand.primary}`,
  boxShadow: `0 0 0 3px ${theme.tokens.border.focus.brand.halo}`
})

const successFieldset = (theme: Theme): CSSObject => ({
  border: `1px solid ${theme.tokens.border.success.primary}`,
  boxShadow: `0 0 0 3px ${theme.tokens.border.success.halo}`
})

const errorFieldset = (theme: Theme): CSSObject => ({
  border: `1px solid ${theme.tokens.border.error.primary}`,
  boxShadow: `0 0 0 3px ${theme.tokens.border.error.halo}`
})

export const StyledSelect = styled(Select)<{ color?: color }>(({ theme, color }) => ({
  '&.MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: theme.borderRadius.medium,
    fontSize: '0.875rem',
    width: '100%',
    lineHeight: '24px',
    color: theme.tokens.foreground.main.primary,
    '>div': {
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      '>div': {
        minWidth: 'auto',
        paddingRight: '8px'
      },
      '&:has(div)': {
        paddingLeft: '16px'
      }
    },
    '>div[aria-expanded="true"] ~ svg': {
      transform: 'rotate(180deg)'
    },
    svg: {
      position: 'absolute',
      right: '7px',
      pointerEvents: 'none'
    },
    fieldset: {
      border: defaultFieldsetBorder(theme)
    },
    '&:hover': {
      backgroundColor: theme.tokens.background.main.pressed,
      border: 'none',
      ...(color !== 'success' &&
        color !== 'error' && {
          fieldset: {
            border: defaultFieldsetBorder(theme)
          }
        }),
      ...(color === 'success' && {
        fieldset: successFieldset(theme)
      }),
      ...(color === 'error' && {
        fieldset: errorFieldset(theme)
      })
    },
    '.select-placeholder': {
      color: theme.tokens.foreground.main.secondary
    }
  },
  '&.MuiInputBase-root.MuiOutlinedInput-root:has(div > .select-placeholder)': {
    backgroundColor: theme.tokens.background.tonal.soft.default
  },
  '&.MuiInputBase-root.MuiOutlinedInput-root:not(:has(div > .select-placeholder))': {
    backgroundColor: theme.tokens.background.main.default
  },
  '&.MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: theme.tokens.background.main.pressed,
    color: theme.tokens.foreground.main.disabled
  },
  '.MuiSelect-icon': {
    top: 'unset',
    color: theme.tokens.foreground.main.primary
  },
  ...StyledSelectFocus(theme, color)
}))

const StyledSelectFocus = (theme: Theme, color?: color): CSSObject => ({
  '&.MuiInputBase-root.MuiOutlinedInput-root.Mui-focused': {
    backgroundColor: theme.tokens.background.main.default,
    ...(color !== 'success' &&
      color !== 'error' && {
        fieldset: focusFieldset(theme)
      }),
    ...(color === 'success' && {
      fieldset: successFieldset(theme)
    }),
    ...(color === 'error' && {
      fieldset: errorFieldset(theme)
    })
  }
})

export const dropdownPaperStyles = (theme: Theme): CSSObject => ({
  backgroundColor: theme.tokens.background.main.default,
  borderRadius: theme.borderRadius.xxLarge,
  boxShadow: theme.elevation.mediumDepth,
  color: 'inherit',
  padding: theme.spacing(6)
})

export const dropdownMenuListStyles = (theme: Theme): CSSObject => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  margin: 0,
  borderRadius: theme.borderRadius.xxLarge,
  '.MuiListSubheader-root': {
    width: '100%',
    backgroundColor: 'transparent',
    color: theme.tokens.foreground.main.secondary,
    padding: '12px 0px 8px',
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.h5.fontWeight,
    fontFamily: theme.typography.h5.fontFamily,
    letterSpacing: theme.typography.h5.letterSpacing,
    lineHeight: theme.typography.h5.lineHeight
  }
})

export const StyledMessage = styled('div')(
  ({ theme }): CSSObject => ({
    color: theme.tokens.foreground.main.primary,
    display: 'flex',
    alignItems: 'center',
    fontFamily: theme.typography.body3.fontFamily,
    fontSize: theme.typography.body3.fontSize,
    marginTop: theme.spacing(2),
    paddingLeft: '6px',
    span: {
      paddingLeft: '6px'
    }
  })
)
